/**
 * Long-term memory over the live E2E relay (v0.5.0) — pure, testable helpers.
 *
 * AppState uses these so live mode validates daemon responses exactly like the
 * loopback `/memories` client: malformed responses become real errors (never a
 * partial/fake list), and failures surface the daemon's fixed message + code.
 */
import type { MemoryManagementOp } from '@luminis/protocol';

import { memoryEntriesFromUnknown, type MemoryEntry } from '../memory';

export interface MemoryLiveResult {
  ok: boolean;
  data?: unknown;
  error?: { code: string; message: string };
}

/** Injected transport: `LiveRelayController.requestMemory` in production. */
export type MemoryLiveRequester = (
  op: MemoryManagementOp,
  id?: string,
) => Promise<MemoryLiveResult>;

/** Fixed-message error text for a failed daemon response (code is appended). */
export function memoryLiveRequestError(result: MemoryLiveResult): Error {
  const message = result.error?.message ?? '长期记忆请求失败';
  return new Error(
    result.error?.code === undefined ? message : `${message}（${result.error.code}）`,
  );
}

async function requireOkData(
  request: MemoryLiveRequester,
  op: MemoryManagementOp,
  id?: string,
): Promise<unknown> {
  const result = await request(op, id);
  if (!result.ok) throw memoryLiveRequestError(result);
  return result.data;
}

function parseMemories(data: unknown, label: string): MemoryEntry[] {
  const entries = memoryEntriesFromUnknown(data);
  if (entries === null) throw new Error(`daemon ${label} 返回了意外数据`);
  return entries;
}

export async function requestMemoryListLive(request: MemoryLiveRequester): Promise<MemoryEntry[]> {
  return parseMemories(await requireOkData(request, 'list'), '/memories');
}

export async function requestMemoryDeleteLive(
  request: MemoryLiveRequester,
  id: string,
): Promise<MemoryEntry[]> {
  return parseMemories(await requireOkData(request, 'delete', id), '/memories');
}

export async function requestMemoryApproveLive(
  request: MemoryLiveRequester,
  id: string,
): Promise<MemoryEntry[]> {
  return parseMemories(await requireOkData(request, 'approve', id), '/memories');
}

/** Clear-all only succeeds after an explicit `{ ok: true }` acknowledgement. */
export async function requestMemoryClearLive(request: MemoryLiveRequester): Promise<{ ok: true }> {
  const data = await requireOkData(request, 'clear');
  const acknowledged =
    data !== null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    (data as Record<string, unknown>)['ok'] === true;
  if (!acknowledged) throw new Error('daemon 未确认清空');
  return { ok: true };
}

/** Availability of the Settings memory section for the current transport. */
export interface MemoryLiveAvailability {
  available: boolean;
  reason?: string;
}

/**
 * Demo mode keeps the mock client; live mode needs pairing + an enabled E2E
 * relay plan (now that `memory.request` exists). A temporarily offline relay
 * stays "available": the request path surfaces the real error instead of
 * hiding the section.
 */
export function memoryLiveAvailability(input: {
  hasClient: boolean;
  demoMode: boolean;
  paired: boolean;
  relayEnabled: boolean;
  relayReason?: string;
}): MemoryLiveAvailability {
  if (input.hasClient) return { available: true };
  if (input.demoMode) {
    return { available: false, reason: '演示模式尚未初始化本地设备（请先完成配对）' };
  }
  if (!input.paired) {
    return { available: false, reason: '长期记忆需要通过已配对的中继访问 daemon（请先配对）' };
  }
  if (!input.relayEnabled) {
    return {
      available: false,
      reason: `live relay 未就绪，无法访问 daemon 的长期记忆接口：${input.relayReason ?? '未配置'}`,
    };
  }
  return { available: true };
}
